import type { ReactNode } from 'react';
import { ensureArray } from '../../utils/array';
import { SWIFTFOX_PALETTE } from '../../palette';
import { Icon, type IconName } from '../icon';
import { Paper, type PaperProps } from '../paper';
import { Stack } from '../stack';
import { Typography } from '../typography';

const brandColors = {
	teal: {
		light: SWIFTFOX_PALETTE.teal['10-arcticBreeze'],
		dark: SWIFTFOX_PALETTE.teal['50-oceanTeal'],
		standardBorder: SWIFTFOX_PALETTE.teal['40-blueReef'],
		filledBorder: SWIFTFOX_PALETTE.teal['20-coolSky']
	},
	green: {
		light: SWIFTFOX_PALETTE.green['10-mintFrost'],
		dark: SWIFTFOX_PALETTE.green['50-freshMeadow'],
		standardBorder: SWIFTFOX_PALETTE.green['40-meadowLight'],
		filledBorder: SWIFTFOX_PALETTE.green['20-springMist']
	},
	magenta: {
		light: SWIFTFOX_PALETTE.magenta['10-lavendar'],
		dark: SWIFTFOX_PALETTE.magenta['50-berryWine'],
		standardBorder: SWIFTFOX_PALETTE.magenta['20-blushPetal'],
		filledBorder: SWIFTFOX_PALETTE.magenta['40-crimsonRose']
	},
	purple: {
		light: SWIFTFOX_PALETTE.purple['10-lilacMist'],
		dark: SWIFTFOX_PALETTE.purple['50-amethyst'],
		standardBorder: SWIFTFOX_PALETTE.purple['20-softViolet'],
		filledBorder: SWIFTFOX_PALETTE.purple['40-royalBloom']
	},
	blue: {
		light: SWIFTFOX_PALETTE.blue['10-icyMist'],
		dark: SWIFTFOX_PALETTE.blue['40-coreBlue'],
		standardBorder: SWIFTFOX_PALETTE.blue['20-frostedAzure'],
		filledBorder: SWIFTFOX_PALETTE.blue['30-skywave']
	},
	orange: {
		light: SWIFTFOX_PALETTE.orange['10-apricotCream'],
		dark: SWIFTFOX_PALETTE.orange['40-emberBaze'],
		standardBorder: SWIFTFOX_PALETTE.orange['20-goldenSand'],
		filledBorder: SWIFTFOX_PALETTE.orange['30-highlight']
	}
};

export interface ChartTallyProps {
	value: ReactNode;
	label: string;
	iconName?: IconName;
	badge?: string;
	tone?: 'neutral' | 'paper-colour' | 'transparent' | keyof typeof brandColors;
	variant?: 'standard' | 'filled';
	centered?: boolean;
	labelNoWrap?: boolean;
	sx?: PaperProps['sx'];
}

export function ChartTally({
	value,
	label,
	iconName,
	badge,
	tone = 'neutral',
	variant = 'standard',
	centered = Boolean(badge),
	labelNoWrap = false,
	sx
}: ChartTallyProps) {
	const palette = tone === 'neutral' || tone === 'paper-colour' || tone === 'transparent' ? undefined : brandColors[tone];
	const colors = palette
		? {
				background: variant === 'filled' ? palette.dark : palette.light,
				text: variant === 'filled' ? palette.light : palette.dark,
				border: variant === 'filled' ? palette.filledBorder : palette.standardBorder
			}
		: undefined;
	return (
		<Paper
			elevation={tone === 'neutral' ? 3 : 0}
			variant={colors || tone === 'paper-colour' ? 'outlined' : 'elevation'}
			sx={[
				{
					display: 'flex',
					alignItems: 'stretch',
					gap: 2,
					p: badge || colors ? 3 : 2,
					minHeight: badge ? 104 : 88,
					boxSizing: 'border-box',
					boxShadow: 'none',
					borderRadius: theme => `${theme.shape['radius-md']}px`,
					bgcolor:
						tone === 'transparent' ? 'transparent' : tone === 'paper-colour' ? 'background.paper-colour' : 'background.paper-elevation-3',
					backgroundImage: 'none',
					...(tone === 'paper-colour' ? { borderColor: 'elevation.outlined' } : {}),
					...(colors ? { bgcolor: colors.background, color: colors.text, borderColor: colors.border } : {})
				},
				...ensureArray(sx)
			]}
		>
			{badge ? (
				<Stack
					justifyContent="center"
					alignItems="center"
					sx={{
						p: 2,
						flexShrink: 0,
						width: 72,
						height: 72,
						alignSelf: 'center',
						boxSizing: 'border-box',
						borderRadius: theme => `${theme.shape['radius-sm']}px`,
						bgcolor: colors?.text ?? 'action.active',
						color: colors?.background ?? 'primary.contrastText'
					}}
				>
					<Typography variant="h5" color="inherit">
						{badge}
					</Typography>
				</Stack>
			) : null}
			<Stack gap={2} justifyContent="center" alignItems={centered ? 'center' : 'flex-start'} sx={{ p: 2, flex: 1, minWidth: 0 }}>
				<Typography variant="h5" color="inherit">
					{value}
				</Typography>
				<Stack
					direction="row"
					gap={2}
					alignItems="flex-start"
					justifyContent={centered ? 'center' : 'flex-start'}
					sx={{ width: labelNoWrap ? 'max-content' : '100%' }}
				>
					{iconName ? <Icon iconName={iconName} /> : null}
					<Typography
						variant="body1"
						color="inherit"
						textAlign={centered ? 'center' : 'left'}
						sx={{ whiteSpace: labelNoWrap ? 'nowrap' : undefined }}
					>
						{label}
					</Typography>
				</Stack>
			</Stack>
		</Paper>
	);
}
